import supabase from '../lib/supabase';

export const authService = {
  // Test connection
  async testConnection() {
    try {
      const { data, error } = await supabase
        .from('admin_users_bp2025')
        .select('count')
        .limit(1);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Initialize admin table and demo user
  async initializeAdmin() {
    try {
      // Check if table exists and has data
      const { data: existingUsers, error: checkError } = await supabase
        .from('admin_users_bp2025')
        .select('id')
        .limit(1);

      if (checkError && checkError.code === '42P01') {
        // Table doesn't exist, create it using direct SQL
        const { error: createError } = await supabase.rpc('exec_sql', {
          sql: `
            CREATE TABLE IF NOT EXISTS admin_users_bp2025 (
              id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
              username TEXT UNIQUE NOT NULL,
              password_hash TEXT NOT NULL,
              full_name TEXT NOT NULL,
              email TEXT UNIQUE NOT NULL,
              created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
              updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
            );

            ALTER TABLE admin_users_bp2025 ENABLE ROW LEVEL SECURITY;

            DROP POLICY IF EXISTS "Allow all operations on admin_users_bp2025" ON admin_users_bp2025;
            CREATE POLICY "Allow all operations on admin_users_bp2025" 
            ON admin_users_bp2025 
            FOR ALL 
            USING (true) 
            WITH CHECK (true);
          `
        });

        if (createError) {
          console.error('Table creation error:', createError);
        }
      }

      // Ensure demo admin user exists for Best Podiatrist
      const { data: upsertData, error: upsertError } = await supabase
        .from('admin_users_bp2025')
        .upsert({
          username: 'admin',
          password_hash: 'admin123',
          full_name: 'Dr. Amit Kumar C Jain',
          email: 'admin@bestpodiatrist.com'
        }, { 
          onConflict: 'username',
          ignoreDuplicates: true 
        })
        .select();

      if (upsertError) {
        console.error('Upsert error:', upsertError);
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Login admin user
  async login(username, password) {
    try {
      // Test connection first
      const connectionTest = await this.testConnection();
      if (!connectionTest.success) {
        console.warn('Connection failed, using fallback mode');
      }

      // Initialize admin table if needed
      await this.initializeAdmin();

      // Fetch admin user
      const { data: adminUser, error } = await supabase
        .from('admin_users_bp2025')
        .select('*')
        .eq('username', username)
        .single();

      if (error) {
        // Fallback to demo credentials if connection fails
        if (username === 'admin' && password === 'admin123') {
          const sessionData = {
            id: 'demo-admin-id',
            username: 'admin',
            fullName: 'Dr. Amit Kumar C Jain',
            email: 'admin@bestpodiatrist.com',
            loginTime: new Date().toISOString()
          };

          localStorage.setItem('adminSession', JSON.stringify(sessionData));
          return { success: true, user: sessionData };
        }
        throw new Error('Invalid credentials or connection issue');
      }

      if (!adminUser) {
        throw new Error('Invalid credentials');
      }

      // Validate password
      const isValidPassword = password === adminUser.password_hash;

      if (!isValidPassword) {
        throw new Error('Invalid credentials');
      }

      // Store admin session in localStorage
      const sessionData = {
        id: adminUser.id,
        username: adminUser.username,
        fullName: adminUser.full_name,
        email: adminUser.email,
        loginTime: new Date().toISOString()
      };

      localStorage.setItem('adminSession', JSON.stringify(sessionData));
      
      return { success: true, user: sessionData };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Check if admin is logged in
  isAuthenticated() {
    const session = localStorage.getItem('adminSession');
    return !!session;
  },

  // Get current admin session
  getCurrentUser() {
    const session = localStorage.getItem('adminSession');
    return session ? JSON.parse(session) : null;
  },

  // Logout admin
  logout() {
    try {
      localStorage.removeItem('adminSession');
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Update admin profile
  async updateProfile(userId, profileData) {
    try {
      // Test connection first
      const connectionTest = await this.testConnection();
      if (!connectionTest.success) {
        throw new Error('Connection failed. Please check your connection and try again.');
      }

      // Update in system
      const { data, error } = await supabase
        .from('admin_users_bp2025')
        .update({
          full_name: profileData.fullName,
          email: profileData.email,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)
        .select()
        .single();

      if (error) {
        // Fallback to local session update for demo mode
        const currentSession = this.getCurrentUser();
        if (currentSession && userId === 'demo-admin-id') {
          const updatedSession = {
            ...currentSession,
            fullName: profileData.fullName,
            email: profileData.email
          };
          localStorage.setItem('adminSession', JSON.stringify(updatedSession));
          return { success: true, user: updatedSession };
        }
        
        throw new Error(`Update failed: ${error.message}`);
      }

      // Update local session immediately
      const currentSession = this.getCurrentUser();
      if (currentSession) {
        const updatedSession = {
          ...currentSession,
          fullName: data.full_name,
          email: data.email
        };
        localStorage.setItem('adminSession', JSON.stringify(updatedSession));
      }

      return { success: true, user: data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Change admin password
  async changePassword(userId, currentPassword, newPassword) {
    try {
      // Test connection first
      const connectionTest = await this.testConnection();
      if (!connectionTest.success) {
        throw new Error('Connection failed. Please check your connection and try again.');
      }

      // First verify current password by fetching user
      const { data: currentUser, error: fetchError } = await supabase
        .from('admin_users_bp2025')
        .select('password_hash')
        .eq('id', userId)
        .single();

      if (fetchError) {
        // Fallback check for demo
        if (userId === 'demo-admin-id' && currentPassword !== 'admin123') {
          throw new Error('Current password is incorrect');
        } else if (userId !== 'demo-admin-id') {
          throw new Error(`Failed to verify current password: ${fetchError.message}`);
        }
      } else {
        // Verify current password matches
        if (currentPassword !== currentUser.password_hash) {
          throw new Error('Current password is incorrect');
        }
      }

      // Update password
      const { data, error: updateError } = await supabase
        .from('admin_users_bp2025')
        .update({
          password_hash: newPassword, // In production, this should be hashed
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)
        .select()
        .single();

      if (updateError) {
        // For demo, still return success if demo credentials work
        if (userId === 'demo-admin-id' && currentPassword === 'admin123') {
          return { success: true };
        }
        throw new Error(`Password update failed: ${updateError.message}`);
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get admin profile
  async getProfile(userId) {
    try {
      // Test connection first
      const connectionTest = await this.testConnection();
      if (!connectionTest.success) {
        throw new Error('Connection failed');
      }

      const { data, error } = await supabase
        .from('admin_users_bp2025')
        .select('id, username, full_name, email, created_at, updated_at')
        .eq('id', userId)
        .single();

      if (error) {
        // Fallback to local session for demo mode
        if (userId === 'demo-admin-id') {
          const currentSession = this.getCurrentUser();
          if (currentSession) {
            return { 
              success: true, 
              user: {
                id: currentSession.id,
                username: currentSession.username,
                full_name: currentSession.fullName,
                email: currentSession.email
              }
            };
          }
        }
        
        return { success: false, error: error.message };
      }

      return { success: true, user: data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Subscribe to real-time admin profile changes
  subscribeToProfileChanges(userId, callback) {
    try {
      const subscription = supabase
        .channel(`admin-profile-${userId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'admin_users_bp2025',
            filter: `id=eq.${userId}`
          },
          (payload) => {
            if (callback && payload.new) {
              callback(payload.new);
            }
            
            // Update local session immediately
            const currentSession = this.getCurrentUser();
            if (currentSession && payload.new) {
              const updatedSession = {
                ...currentSession,
                fullName: payload.new.full_name,
                email: payload.new.email
              };
              localStorage.setItem('adminSession', JSON.stringify(updatedSession));
            }
          }
        )
        .subscribe();

      return subscription;
    } catch (error) {
      return null;
    }
  },

  // Connection monitoring
  async monitorConnection() {
    try {
      const { data, error } = await supabase
        .from('admin_users_bp2025')
        .select('count')
        .limit(1);

      return { 
        connected: !error, 
        error: error?.message,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return { 
        connected: false, 
        error: error.message,
        timestamp: new Date().toISOString()
      };
    }
  },

  // Get all admin users (for debugging)
  async getAllAdminUsers() {
    try {
      const { data, error } = await supabase
        .from('admin_users_bp2025')
        .select('id, username, full_name, email, created_at, updated_at');

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, users: data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
};